/** Labels and tones for tachograph enums (English source strings; translate with t()). */
import { formatDate, formatDuration } from '@/lib/format';
import { msg, t } from '@/lib/i18n';
import type { TimelineSegment, Tone } from '@/ui';

type TimelineTone = NonNullable<TimelineSegment['tone']>;

type Label = { label: string; tone: Tone; description?: string };

/** EU 561 activities (the tachograph's four states). */
export const ACTIVITY: Record<string, Label & { timeline: TimelineTone }> = {
  drive: { label: msg('Driving'), tone: 'info', timeline: 'primary' },
  work: { label: msg('Other work'), tone: 'medium', timeline: 'medium' },
  available: { label: msg('Available'), tone: 'accent', timeline: 'accent' },
  rest: { label: msg('Rest'), tone: 'good', timeline: 'good' },
  unknown: { label: msg('Unknown'), tone: 'neutral', timeline: 'neutral' },
};

/** Infringement categories of Regulation (EU) 2016/403: minor → most serious. */
export const SEVERITY: Record<string, Label> = {
  MI: { label: msg('Minor'), tone: 'info', description: msg('Minor infringement (MI)') },
  SI: { label: msg('Serious'), tone: 'medium', description: msg('Serious infringement (SI)') },
  VSI: { label: msg('Very serious'), tone: 'caution', description: msg('Very serious infringement (VSI)') },
  MSI: { label: msg('Most serious'), tone: 'bad', description: msg('Most serious infringement (MSI)') },
  unclassified: { label: msg('Not classified'), tone: 'neutral', description: msg('Not classified (below the first band)') },
};
export const SEVERITY_ORDER = ['MSI', 'VSI', 'SI', 'MI'] as const;

export const INFRINGEMENT_STATUS: Record<string, Label> = {
  open: { label: msg('Open'), tone: 'medium' },
  acknowledged: { label: msg('Acknowledged'), tone: 'neutral' },
  withdrawn: { label: msg('Withdrawn'), tone: 'neutral' },
};

export const DOWNLOAD_STATUS: Record<string, Label> = {
  scheduled: { label: msg('Scheduled'), tone: 'neutral' },
  requested: { label: msg('Requested'), tone: 'info' },
  in_progress: { label: msg('In progress'), tone: 'accent' },
  completed: { label: msg('Completed'), tone: 'good' },
  failed: { label: msg('Failed'), tone: 'bad' },
  timed_out: { label: msg('Timed out'), tone: 'caution' },
};
export const OPEN_DOWNLOAD = new Set(['scheduled', 'requested', 'in_progress']);

export const TARGET_KIND: Record<string, string> = {
  driver_card: msg('Driver card'),
  vehicle_unit: msg('Vehicle unit'),
};

export const TRIGGER: Record<string, string> = { manual: msg('Manual'), schedule: msg('Schedule'), retry: msg('Automatic retry') };
export const FILE_SOURCE: Record<string, string> = { remote: msg('Remote download'), upload: msg('Upload'), import: msg('Import') };

export const PARSE_STATUS: Record<string, Label> = {
  parsed: { label: msg('Parsed'), tone: 'good' },
  unparsed: { label: msg('Stored, not parsed'), tone: 'neutral' },
  failed: { label: msg('Parse failed'), tone: 'bad' },
};

export const CARD_STATUS: Record<string, Label> = {
  active: { label: msg('Active'), tone: 'good' },
  suspended: { label: msg('Suspended'), tone: 'caution' },
  withdrawn: { label: msg('Withdrawn'), tone: 'neutral' },
  expired: { label: msg('Expired'), tone: 'bad' },
};

export const CONFIDENCE: Record<string, Label> = {
  high: { label: msg('High confidence'), tone: 'good' },
  medium: { label: msg('Medium confidence'), tone: 'medium' },
  low: { label: msg('Low confidence'), tone: 'caution' },
};

/** Warnings from the hours-left engine. */
export const WARNING: Record<string, Label> = {
  break_due_soon: { label: msg('Break due soon'), tone: 'medium' },
  break_overdue: { label: msg('Break overdue'), tone: 'bad' },
  daily_limit_soon: { label: msg('Daily limit soon'), tone: 'medium' },
  daily_limit_reached: { label: msg('Daily limit reached'), tone: 'bad' },
  weekly_rest_due_soon: { label: msg('Weekly rest due soon'), tone: 'medium' },
  daily_rest_due_soon: { label: msg('Daily rest due soon'), tone: 'medium' },
  stale: { label: msg('No live data'), tone: 'neutral' },
};

export function labelOf(map: Record<string, { label: string } | string>, key: string | null | undefined): string {
  if (!key) return '—';
  const v = map[key];
  if (!v) return key.replace(/_/g, ' ');
  return t(typeof v === 'string' ? v : v.label);
}

export function toneOf(map: Record<string, { tone: Tone }>, key: string | null | undefined): Tone {
  return (key && map[key]?.tone) || 'neutral';
}

/** EU 561 limits used as the "full" length of the hours-left bars (seconds). */
export const LIMITS = {
  continuous: 4.5 * 3600,
  daily: 9 * 3600,
  dailyExtended: 10 * 3600,
  weekly: 56 * 3600,
  fortnight: 90 * 3600,
};

/** Remaining time → tone: ≤ 15 % of the limit red, ≤ 35 % amber, else green (old console). */
export function remainingTone(left: number, limit: number): 'good' | 'medium' | 'bad' {
  const r = limit > 0 ? left / limit : 0;
  return r <= 0.15 ? 'bad' : r <= 0.35 ? 'medium' : 'good';
}

/** "10 h 42 min / 10 h" and how far over or short it is, per rule kind. */
export function valueVsLimit(value: number, limit: number): { text: string; delta: string } {
  const text = `${formatSpan(value)} / ${formatSpan(limit)}`;
  const d = value - limit;
  if (Math.abs(d) < 60) return { text, delta: '' };
  return { text, delta: d > 0 ? t('{duration} over', { duration: formatSpan(d) }) : t('{duration} short', { duration: formatSpan(-d) }) };
}

/** Driving and rest spans in hours and minutes, also beyond a day ("143 h 20 min", never "5 d 23 h"). */
export function formatSpan(seconds: number): string {
  if (seconds < 24 * 3600) return formatDuration(seconds);
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? t('{hours} h {minutes} min', { hours, minutes: rest }) : t('{hours} h', { hours });
}

/** "3 Sep – 30 Sep 2026" for a file's covered period. */
export function filePeriod(f: { period_start?: string | null; period_end?: string | null }): string {
  if (!f.period_start || !f.period_end) return '—';
  return `${formatDate(f.period_start, 'short')} – ${formatDate(f.period_end, 'date')}`;
}

/** Time left: "0 min" when nothing is left (never "0 s"); weekly amounts in hours ("45 h"), not days. */
export function formatLeft(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  if (seconds < 60) return t('0 min');
  if (seconds >= 24 * 3600) return t('{hours} h', { hours: Math.floor(seconds / 3600) });
  return formatDuration(seconds);
}
